import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
	CasterHousing,
	CasterLowTonnageReason,
	CasterMoldSize,
	CasterPourbackReason,
	CasterScrapReason,
	CasterTundish,
	CasterTundishShroud,
	Grade,
	Ladle,
} from "../models/Catalogs";
import { ChemistrySample } from "../models/ElementData";
import { ProbeData } from "../models/ProbeData";
import { ProcessData } from "../models/ProcessData";
import { ProcessWithoutDeliveryTicket } from "../models/ProcessWithoutDeliveryTicket";
import { ScrapData } from "../models/ScrapData";
import { StrandData } from "../models/StrandData";
import { StrandTimeSeriesData } from "../models/StrandSpeed";
import { LostStrand } from "../models/caster.models";

const CastersInitialState: {
	isLoadingPage: boolean;
	isLoadingBackground: boolean;
	errorMessage: string;
	notificationMessage: string;
	refreshStatus: string;
	lazyStart: Date | null;
	processId: number | null | false;
	processData: ProcessData | null;
	canRefresh: boolean;
	refreshInPause: boolean;
	tundishes: CasterTundish[];
	strands: StrandData[] | null;
	scraps: ScrapData[] | null;
	elements: ChemistrySample[];
	strandSpeeds: StrandTimeSeriesData[];
	moldLevels: StrandTimeSeriesData[];
	billetTemp: StrandTimeSeriesData[];
	probes: ProbeData[] | null;
	lostStrands: LostStrand[] | null;
	pourBacks: CasterPourbackReason[] | null;
	moldSizes: CasterMoldSize[] | null;
	tundishShrouds: CasterTundishShroud[] | null;
	tonnageReasons: CasterLowTonnageReason[] | null;
	scrapReasons: CasterScrapReason[] | null;
	housings: CasterHousing[] | null;
	ladles: Ladle[] | null;
	grades: Grade[] | null;
	processesWithoutDeliveryTicket: ProcessWithoutDeliveryTicket[];
	//Refatoring
	entityEditting: {
		entity: "PROCESS" | "STRAND" | "PROBE" | null;
		index: number;
		field: string;
	} | null;
} = {
	isLoadingPage: false,
	isLoadingBackground: false,
	errorMessage: "",
	notificationMessage: "",
	refreshStatus: "",
	lazyStart: null,
	processId: false,
	processData: null,
	canRefresh: true,
	refreshInPause: false,
	tundishes: [],
	strands: null,
	scraps: null,
	elements: [],
	strandSpeeds: [],
	moldLevels: [],
	billetTemp: [],
	probes: null,
	lostStrands: null,
	pourBacks: null,
	moldSizes: null,
	tundishShrouds: null,
	tonnageReasons: null,
	scrapReasons: null,
	housings: null,
	ladles: null,
	grades: null,
	processesWithoutDeliveryTicket: [],
	//Refatoring
	entityEditting: null,
};

export const CastersReducer = createSlice({
	name: "__",
	initialState: CastersInitialState,
	reducers: {
		setRefreshInPause(state, { payload }) {
			if (typeof payload == "function") {
				state.refreshInPause = payload(state.refreshInPause);
			} else {
				state.refreshInPause = payload;
			}
		},
		setIsLoadingPage(state, { payload }) {
			state.isLoadingPage = payload;
		},
		setIsLoadingBackground(state, { payload }) {
			state.isLoadingBackground = payload;
		},
		setErrorMessage(state, { payload }) {
			state.errorMessage = payload;
		},
		setNotificationMessage(state, { payload }) {
			state.notificationMessage = payload;
		},
		setProcessId(state, { payload }) {
			state.processId = payload;
		},
		setProcess(state, { payload }) {
			const {
				processData,
				strands,
				probes,
				elements,
				lostStrands,
				strandSpeeds,
				moldLevels,
				billetTemp,
				scraps,
			} = payload;
			state.processData = processData;
			state.strands = strands;
			state.probes = probes;
			state.elements = elements;
			state.lostStrands = lostStrands;
			state.strandSpeeds = strandSpeeds;
			state.moldLevels = moldLevels;
			state.billetTemp = billetTemp;
			state.scraps = scraps;
		},
		setProbes(state, { payload }) {
			state.probes = payload;
		},
		setCatalogs(state, { payload }) {
			const {
				tundishes,
				pourBacks,
				moldSizes,
				tundishShrouds,
				tonnageReasons,
				scrapReasons,
				housings,
				ladles,
				grades,
			} = payload;
			state.tundishes = tundishes;
			state.pourBacks = pourBacks;
			state.moldSizes = moldSizes;
			state.tundishShrouds = tundishShrouds;
			state.tonnageReasons = tonnageReasons;
			state.scrapReasons = scrapReasons;
			state.housings = housings;
			state.ladles = ladles;
			state.grades = grades;
		},
		addRefreshStatus(state, { payload }) {
			state.refreshStatus = payload;
		},
		//Refatoring
		mergeProcess(
			state,
			action: PayloadAction<{
				processData: ProcessData;
				strands: StrandData[];
				probes: ProbeData[];
				elements: ChemistrySample[];
				lostStrands: LostStrand[];
				strandSpeeds: StrandTimeSeriesData[];
				moldLevels: StrandTimeSeriesData[];
				billetTemp: StrandTimeSeriesData[];
				scraps: ScrapData[];
			}>,
		) {
			const {
				processData,
				strands,
				probes,
				elements,
				lostStrands,
				strandSpeeds,
				moldLevels,
				billetTemp,
				scraps,
			} = action.payload;
			if (state.entityEditting === null) {
				state.processData = processData;
				state.strands = strands;
				state.probes = probes;
				state.elements = elements;
				state.lostStrands = lostStrands;
				state.strandSpeeds = strandSpeeds;
				state.moldLevels = moldLevels;
				state.billetTemp = billetTemp;
				state.scraps = scraps;
			} else {
				const { entity, index, field } = state.entityEditting;
				//Process
				if (state.processData != null && entity === "PROCESS" && field !== "") {
					state.processData = {
						...processData,
						[field]: state.processData[field as keyof ProcessData],
					};
				} else state.processData = processData;
				//Strands
				if (state.strands !== null && entity === "STRAND" && index > -1) {
					const value = state.strands[index][field as keyof StrandData];
					const tempStrands = [...strands];
					tempStrands[index] = { ...state.strands[index], [field]: value };
					state.strands = tempStrands;
				} else state.strands = strands;
				//Probes
				if (state.probes !== null && entity === "PROBE" && index > -1) {
					const tempProbes = [...probes];
					if (state.probes[index]?.ProbeId === 0) {
						//added
					} else {
						const value = state.probes[index][field as keyof ProbeData];
						tempProbes[index] = { ...state.probes[index], [field]: value };
					}
					state.probes = tempProbes;
				} else state.probes = probes;
				state.elements = elements;
				state.lostStrands = lostStrands;
				state.strandSpeeds = strandSpeeds;
				state.moldLevels = moldLevels;
				state.billetTemp = billetTemp;
				state.scraps = scraps;
			}
		},
		setFocusField(
			state,
			action: PayloadAction<{
				entity: "PROCESS" | "STRAND" | "PROBE";
				index: number;
				field: string;
			}>,
		) {
			const { entity, index, field } = action.payload;
			if (entity === "PROCESS" && state.processData === null) return;
			if (
				entity === "STRAND" &&
				(state.strands === null ||
					state.strands[index] === undefined ||
					!state.strands[index].IsStrandDataManual)
			)
				return;
			if (
				entity === "PROBE" &&
				(state.probes === null ||
					state.probes[index] === undefined ||
					!state.probes[index].IsProbeManual)
			)
				return;
			state.canRefresh = false;
			state.entityEditting = { entity: entity, index: index, field };
			state.lazyStart = new Date();
		},
		clearFocusField(state) {
			state.entityEditting = null;
			state.canRefresh = true;
			state.lazyStart = null;
		},
		updateProcessData(
			state,
			action: PayloadAction<{ field: string; value: any; clear?: boolean }>,
		) {
			const { field, value, clear = false } = action.payload;
			if (state.processData === null) return;
			let ladleSuperHeat = state.processData.LadleSuperHeat || 0;
			if (field === "TrimProbeTemperature") {
				ladleSuperHeat =
					(state.processData.F1LiquidusTemperature || 0) -
					Number(value).valueOf();
			}
			state.processData = {
				...state.processData,
				[field]: value,
				LadleSuperHeat: ladleSuperHeat,
			};
			if (clear) {
				state.entityEditting = null;
				state.canRefresh = true;
				state.lazyStart = null;
			}
		},
		setProcessesWithoutDeliveryTicket(state, { payload }) {
			state.processesWithoutDeliveryTicket = payload || [];
		},
	},
});
